import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import Pagination from "../../components/Pagination";
import loader from "../../assets/loader.webp";
import { useNavigate } from "react-router-dom";
import { adminAxios } from "../../axiosInstance";

const fetchAdminProfile = async () => {
  const response = await adminAxios.get("/users/profile");
  return response?.data?.data;
};

const fetchRestockSuggestions = async (page = 1) => {
  const response = await adminAxios.get(`restock?page=${page}`);
  return {
    suggestions: Array.isArray(response?.data?.data) ? response.data.data : [],
    lastPage: response?.data?.last_page || 1,
  };
};

const getStockOutDaysDisplay = (stockOutDays) => {
  if (stockOutDays == null) return null;
  const days = Math.max(0, Math.floor(stockOutDays));
  return { days, isCritical: days < 10 };
};

function RestockPro() {
  const [showModal, setShowModal] = useState(true);
  const [searchData, setSearchData] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const {
    data: profileData,
    isLoading: isProfileLoading,
  } = useQuery({
    queryKey: ["admin-profile"],
    queryFn: fetchAdminProfile,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  // plan may be at top-level or nested under subscription depending on API response
  const planValue = profileData?.plan || profileData?.subscription?.plan || "";
  const isPro = planValue.toLowerCase() === "pro";

  const {
    data: restockData,
    isLoading: isSuggestionsLoading,
    isFetching: isSuggestionsFetching,
    isError,
    error,
  } = useQuery({
    queryKey: ["restock-suggestions", currentPage],
    queryFn: () => fetchRestockSuggestions(currentPage),
    enabled: isPro,
    staleTime: 2 * 60 * 1000,
    retry: false,
    keepPreviousData: true,
  });

  if (error) {
    console.error("[RestockPro] Failed to load restock suggestions:", error);
    console.error("[RestockPro] Error response:", error?.response?.data);
    console.error("[RestockPro] Status:", error?.response?.status);
  }

  const suggestions = restockData?.suggestions || [];
  const totalPage = restockData?.lastPage || 1;

  const filteredSuggestions = suggestions.filter((item) =>
    (item.product_name || "").toLowerCase().includes(searchData.toLowerCase()),
  );

  if (isProfileLoading) {
    return (
      <div className="w-full flex justify-center items-center py-16">
        <img src={loader} alt="loader" className="w-10 lg:w-20" />
      </div>
    );
  }

  if (!isPro) {
    return (
      <div className="lg:overflow-y-hidden">
        <div className="flex justify-between mb-4 items-center">
          <h3 className="text-xl font-semibold">
            <ShowChartIcon /> Restock Pro
          </h3>
        </div>
        <RestockProModal open={showModal} onClose={() => setShowModal(false)} />
        <div className="bg-white flex flex-col items-center justify-center py-16 px-8 text-center text-gray-500">
          Restock Pro is a Pro-plan feature.{" "}
          <button
            className="text-blue-700 font-semibold underline ml-1"
            onClick={() => setShowModal(true)}
          >
            Learn more
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="lg:overflow-y-hidden">
      {/* Header */}
      <div className="flex justify-between mb-4 items-center">
        <h3 className="text-xl font-semibold">
          <ShowChartIcon /> Restock Pro
        </h3>
      </div>

      <div className="bg-white flex flex-col lg:justify-normal w-full lg:gap-3 overflow-x-auto items-center py-6 px-8">
        {/* Search bar */}
        <div className="flex flex-col lg:flex-row justify-between items-center w-full mb-4">
          <input
            type="text"
            className="border-2 border-gray-400 w-60 p-2 px-6"
            placeholder="Search"
            value={searchData}
            onChange={(e) => setSearchData(e.target.value)}
          />
        </div>

        <div className="w-full">
          <table className="min-w-full bg-white border border-gray-300">
            <thead>
              <tr className="bg-gray-200">
                <th className="py-2 px-4 border-b border-gray-300">
                  PRODUCT NAME
                </th>
                <th className="py-2 px-4 border-b border-gray-300">
                  CURRENT STOCK
                </th>
                <th className="py-2 px-4 border-b border-gray-300">
                  LOW STOCK LIMIT
                </th>
                <th className="py-2 px-4 border-b border-gray-300">
                  DAYS UNTIL STOCK-OUT
                </th>
                <th className="py-2 px-4 border-b border-gray-300">
                  AVG DAILY SALES
                </th>
                <th className="py-2 px-4 border-b border-gray-300">
                  STOCK-OUT DATE
                </th>
                <th className="py-2 px-4 border-b border-gray-300">
                  SUGGESTED REORDER QTY
                </th>
              </tr>
            </thead>
            <tbody>
              {isSuggestionsLoading || isSuggestionsFetching ? (
                <tr>
                  <td colSpan={6} className="text-center py-6">
                    <img
                      src={loader}
                      alt="loader"
                      className="w-10 lg:w-20 mx-auto"
                    />
                  </td>
                </tr>
              ) : isError ? (
                <tr>
                  <td colSpan={6} className="text-center py-4 text-gray-500">
                    {error?.response?.data?.message ||
                      "Couldn't load restock suggestions right now."}
                  </td>
                </tr>
              ) : filteredSuggestions.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-4">
                    No data found.
                  </td>
                </tr>
              ) : (
                filteredSuggestions.map((item) => {
                  const stockOutDays = getStockOutDaysDisplay(
                    item.stock_out_days,
                  );
                  return (
                    <tr
                      key={item.id}
                      className="border-t hover:bg-gray-100 border-gray-200"
                    >
                      <td className="py-2 px-4 border-b border-gray-300 text-center">
                        {item.product_name || "-"}
                      </td>
                      <td className="py-2 px-4 border-b border-gray-300 text-center">
                        {item.current_stock ?? "-"}
                      </td>
                      <td className="py-2 px-4 border-b border-gray-300 text-center">
                        {item.low_stock_limit ?? "-"}
                      </td>
                      <td className="py-2 px-4 border-b border-gray-300 text-center">
                        {stockOutDays ? (
                          <span
                            className={
                              stockOutDays.isCritical
                                ? "text-red-600 font-semibold"
                                : ""
                            }
                          >
                            {stockOutDays.days}
                          </span>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="py-2 px-4 border-b border-gray-300 text-center">
                        {item.average_daily_sales ?? "-"}
                      </td>
                      <td className="py-2 px-4 border-b border-gray-300 text-center">
                        {item.stock_out_date
                          ? new Date(item.stock_out_date).toLocaleDateString()
                          : "-"}
                      </td>
                      <td className="py-2 px-4 border-b border-gray-300 text-center">
                        {item.suggested_reorder_qty ?? "-"}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>

          {!isSuggestionsLoading && !isError && totalPage > 1 && (
            <Pagination
              onPageChange={setCurrentPage}
              totalPage={totalPage}
              currentPage={currentPage}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default RestockPro;

function RestockProModal({ open, onClose }) {
  const navigate = useNavigate();

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-40"
      style={{ backdropFilter: "blur(2px)" }}
    >
      <div className="bg-white p-6 rounded-lg max-w-lg shadow-lg w-[90vw] md:w-auto">
        <h2 className="text-xl mb-2 font-bold text-blue-700">Restock Pro</h2>
        <div className="text-gray-700 mb-4">
          <p>
            <span className="font-semibold text-blue-700">Restock Pro</span> is
            available exclusively on the Pro plan.
          </p>
          <p className="mt-3">
            It uses historical sales and inventory trends to estimate stock-out
            dates, suggest reorder quantities, and highlight at-risk products
            before you run out.
          </p>
          <ul className="mt-3 ml-5 list-disc text-gray-600 text-sm">
            <li>
              Get forecasted{" "}
              <span className="font-medium">stock-out dates</span>
            </li>
            <li>
              See{" "}
              <span className="font-medium">
                AI-suggested reorder quantities
              </span>{" "}
              for each low-stock product
            </li>
            <li>
              Optimize restocking to avoid missed sales and overstocking costs
            </li>
          </ul>
          <p className="mt-3">
            <span className="font-semibold text-blue-700">Tip:</span> Upgrade to
            Pro from your profile to unlock Restock Pro for your store.
          </p>
        </div>
        <div className="flex flex-row gap-3 mt-2">
          <button
            className="px-5 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition"
            onClick={onClose}
          >
            Close
          </button>
          <button
            className="px-5 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition"
            onClick={() => {
              onClose();
              navigate("/admin/profile");
            }}
          >
            Upgrade to Pro
          </button>
        </div>
      </div>
    </div>
  );
}
